import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface FormatAvailability {
  format: string;
  enabled: boolean;
  coming_soon_message: string | null;
}

export const DEFAULT_COMING_SOON_MESSAGE =
  "This competition format is launching soon. Stay tuned!";

/**
 * Public-safe availability of each competition format.
 * Backed by the SECURITY DEFINER RPC get_competition_format_availability().
 * Formats not present in the table are treated as enabled (e.g. CHAMPIONS_LEAGUE).
 */
export function useCompetitionFormatSettings() {
  return useQuery({
    queryKey: ["competition-format-availability"],
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    queryFn: async (): Promise<Record<string, FormatAvailability>> => {
      const { data, error } = await supabase.rpc(
        "get_competition_format_availability" as any
      );
      if (error) throw error;
      const map: Record<string, FormatAvailability> = {};
      for (const row of (data as any[]) || []) {
        map[String(row.format)] = {
          format: String(row.format),
          enabled: row.enabled !== false,
          coming_soon_message: row.coming_soon_message ?? null,
        };
      }
      return map;
    },
  });
}

/** Convenience helpers that fail open (treat unknown formats as enabled). */
export function isFormatEnabled(
  map: Record<string, FormatAvailability> | undefined,
  format: string | null | undefined
): boolean {
  if (!format || !map) return true;
  const row = map[format];
  return row ? row.enabled : true;
}

export function formatComingSoonMessage(
  map: Record<string, FormatAvailability> | undefined,
  format: string | null | undefined
): string {
  if (!format || !map) return DEFAULT_COMING_SOON_MESSAGE;
  return map[format]?.coming_soon_message?.trim() || DEFAULT_COMING_SOON_MESSAGE;
}
